import React, { useEffect, useState } from "react";
import moment from "moment";
import { DatePicker } from "antd";

import { CERNER_EHR_WHITELABEL, rangeList } from "../../constants/constants";
import CustomLabel from "../customLabel";
import CustomMultiSelect from "../multiSelect";
import ExportButton from "../../components/exportButton/exportButton";
import SingleSelect from "../select/singleSelect";

const timeZones = [
  { name: "UTC", id: "utc" },
  { name: "CST", id: "cst" },
  { name: "EST", id: "est" },
  { name: "MST", id: "mst" },
  { name: "PST", id: "pst" },
  { name: "ARIZONA-MST", id: "arizona-MST" },
  { name: "AKST", id: "akst" },
  { name: "HST", id: "hst" },
];

const DateFilter = (props) => {
  const {
    downloadCSV,
    getDateFilter,
    showDownload = true,
    start = null,
    end = null,
    range = "all",
    filename,
    className,
  } = props;
  const minimumDate = moment()
    .subtract(6, "months")
    .startOf("day");
  const maximumDate = moment()
    .endOf("day")
    .subtract(1, "days");
  const currentDate = moment().endOf("day");
  const [state, setState] = useState({
    range: range || "all",
    startDate: start || undefined,
    endDate: end || undefined,
    isDownloading: false,
    timeZone: { name: "UTC", id: "utc" },
  });

  const handleRange = (value) => {
    let startDate = undefined;
    let endDate = moment(maximumDate);
    const today = moment();
    if (value === "thisWeek") {
      startDate = moment(today).startOf("week");
      endDate = moment(today).endOf("week");
      if (endDate.isAfter(maximumDate)) endDate = maximumDate;
    } else if (value === "thisMonth") {
      startDate = moment(today).startOf("month");
      endDate = moment(today).endOf("month");
      if (endDate.isAfter(maximumDate)) endDate = maximumDate;
    } else if (value === "last7days") {
      startDate = moment(maximumDate).subtract(6, "days");
    } else if (value === "last15days") {
      startDate = moment(maximumDate).subtract(14, "days");
    } else if (value === "last30days") {
      startDate = moment(maximumDate).subtract(29, "days");
    } else if (value === "last3months") {
      startDate = moment(maximumDate).subtract(3, "months");
    } else if (value === "last6months") {
      startDate = moment(maximumDate).subtract(6, "months");
    } else if (value === "last1Year") {
      startDate = moment(maximumDate).subtract(1, "year");
    } else if (value === "random" || value === "all") {
      startDate = undefined;
      endDate = undefined;
    }

    setState((st) => ({
      ...st,
      range: value,
      startDate: startDate ? moment(startDate).startOf("day") : undefined,
      endDate: endDate ? moment(endDate).endOf("day") : undefined,
    }));
  };

  useEffect(
    () => {
      if (state.range !== "random") {
        getDateFilter(state);
      }
    },
    [state.range]
  );

  useEffect(
    () => {
      if (state.range === "random") {
        getDateFilter(state);
      }
    },
    [state.startDate, state.endDate]
  );

  useEffect(
    () => {
      getDateFilter(state);
    },
    [state.timeZone]
  );

  const handleFrom = (date) => {
    setState((st) => {
      let endDate = st.endDate;
      if (st.endDate && st.endDate.isBefore(date)) {
        endDate = date ? moment(date).endOf("day") : date;
      }
      return {
        ...st,
        startDate: date ? moment(date).startOf("day") : date,
        endDate,
      };
    });
  };

  const handleTo = (date) => {
    setState((st) => ({
      ...st,
      endDate: date ? moment(date).endOf("day") : date,
    }));
  };

  const enableDisableLoading = (isDownloading) => {
    setState((st) => ({ ...st, isDownloading }));
  };

  const handleTimeZone = (value) => {
    setState((st) => ({
      ...st,
      timeZone: value,
    }));
  };

  return (
    <div className={`${className} date-filters-wrapper`}>
      <div className="form-row mr-16">
        <CustomLabel title="Time Zone" />
        <SingleSelect
          optionValueProp="id"
          value={state.timeZone}
          required
          data={timeZones}
          name="timeZone"
          optionLabelProp="name"
          placeholder="Select"
          customClass="mt-2"
          allowClear={false}
          sort={false}
          onChange={handleTimeZone}
        />
      </div>
      <div className="form-row mr-16">
        <CustomLabel title="Date Range" />
        <CustomMultiSelect
          customClass="common-select-field"
          data={rangeList}
          disabled={state.practiceId === " "}
          onChange={handleRange}
          optionLabelProp="value"
          optionValueProp="key"
          placeholder="Select Range"
          value={state.range === "" ? undefined : state.range}
        />
      </div>
      <div className="form-row ml-16 mr-16">
        <CustomLabel title="From" />
        <DatePicker
          format="MM/DD/YYYY"
          disabled={state.range !== "random"}
          onChange={handleFrom}
          style={{ marginLeft: "1.2rem" }}
          value={state.startDate}
          className="common-date-time"
          disabledDate={(date) =>
            date.isBefore(minimumDate) ||
            (date.isAfter(currentDate) &&
              !date.isSame(maximumDate, "day") &&
              !date.isSame(minimumDate, "day"))
          }
        />
      </div>

      <div className="form-row mr-16">
        <CustomLabel title="To" />
        <DatePicker
          format="MM/DD/YYYY"
          onChange={handleTo}
          style={{ marginLeft: "1.2rem" }}
          disabled={state.range !== "random"}
          value={state.endDate}
          className="common-date-time"
          disabledDate={(date) => {
            return (
              (date.isAfter(currentDate) && !date.isSame(maximumDate, "day")) ||
              (state.startDate &&
                date.isBefore(state.startDate) &&
                !date.isSame(state.startDate, "day"))
            );
          }}
        />
      </div>

      <div className="form-row download-btn">
        {showDownload && (
          <ExportButton
            isDownloading={enableDisableLoading}
            fetchReportDataFromAPI={downloadCSV}
            filename={filename}
            className={`${
              CERNER_EHR_WHITELABEL ? "export-button-cerner" : "export-button"
            }`}
          />
        )}
      </div>
    </div>
  );
};
export default DateFilter;
